import { useEffect, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { fmtDate } from '../utils/format';
import {
  listAnnouncements,
  createAnnouncement,
  removeAnnouncement,
  ackAnnouncement,
  announcementAcks,
} from '../api/announcements';
import CustomerAnnounce from './CustomerAnnounce';

export const DEPARTMENTS = ['Phòng kinh doanh', 'Phòng DVKH', 'Phòng CSKH'];
export const DURATIONS = [
  { h: 1, label: '1 giờ' },
  { h: 3, label: '3 giờ' },
  { h: 6, label: '6 giờ' },
  { h: 12, label: '12 giờ' },
  { h: 24, label: '1 ngày' },
  { h: 72, label: '3 ngày' },
  { h: 168, label: '1 tuần' },
  { h: 720, label: '1 tháng' },
  { h: 876000, label: 'Không giới hạn' }, // ~100 năm: thông báo nội bộ chạy thường trực
];

function fmtDateTime(d) {
  const dt = new Date(d);
  return `${fmtDate(dt)} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

// Modal tạo thông báo nội bộ.
function CreateModal({ onClose, onCreated }) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [depts, setDepts] = useState([]); // [] = tất cả phòng ban
  const [hours, setHours] = useState(24);
  const [saving, setSaving] = useState(false);

  const toggleDept = (d) => setDepts((arr) => (arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]));

  async function save() {
    if (saving) return;
    if (!title.trim()) return toast('Nhập tiêu đề thông báo', 'warn');
    if (!content.trim()) return toast('Nhập nội dung thông báo', 'warn');
    setSaving(true);
    try {
      await createAnnouncement({ title: title.trim(), content: content.trim(), departments: depts, durationHours: hours });
      toast('Đã tạo thông báo nội bộ');
      onCreated();
      onClose();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h3>📣 Tạo thông báo nội bộ</h3>

      <div className="field">
        <label>Tiêu đề</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tiêu đề thông báo" />
      </div>

      <div className="field">
        <label>Nội dung thông báo</label>
        <textarea
          rows={3}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Nội dung sẽ chạy trên dòng chạy ở đầu màn hình…"
        />
      </div>

      <div className="field">
        <label>Gửi tới phòng ban</label>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className={`filterBtn${depts.length === 0 ? ' on' : ''}`}
            onClick={() => setDepts([])}
          >
            Tất cả
          </button>
          {DEPARTMENTS.map((d) => (
            <button
              type="button"
              key={d}
              className={`filterBtn${depts.includes(d) ? ' on' : ''}`}
              onClick={() => toggleDept(d)}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="small muted" style={{ marginTop: 6 }}>
          Không chọn phòng nào = gửi tới tất cả phòng ban.
        </div>
      </div>

      <div className="field">
        <label>Thời gian chạy trên dòng chạy</label>
        <select style={{ width: 'auto', minWidth: 160 }} value={hours} onChange={(e) => setHours(Number(e.target.value))}>
          {DURATIONS.map((d) => (
            <option key={d.h} value={d.h}>
              {d.label}
            </option>
          ))}
        </select>
      </div>

      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <button className="btn ghost" onClick={onClose}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Tạo thông báo'}
        </button>
      </div>
    </div>
  );
}

// Modal đọc toàn bộ nội dung + nút xác nhận đã tiếp nhận.
function DetailModal({ item, me, onClose, onChanged }) {
  const toast = useToast();
  const [acked, setAcked] = useState(item.mineAcked);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    try {
      await ackAnnouncement(item._id);
      setAcked(true);
      toast('Đã xác nhận tiếp nhận thông báo');
      onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h3>📣 {item.title || 'Thông báo'}</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        {item.departments && item.departments.length ? item.departments.join(', ') : 'Tất cả phòng ban'} · Người tạo:{' '}
        {item.createdByName || '—'} · Chạy đến {fmtDateTime(item.endAt)}
      </div>
      <div className="card" style={{ background: 'var(--panel2)', whiteSpace: 'pre-wrap', marginBottom: 16 }}>
        {item.content}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
        {item.canAck &&
          (acked ? (
            <button className="btn ok" disabled>
              ✓ Bạn đã tiếp nhận
            </button>
          ) : (
            <button className="btn" onClick={confirm} disabled={busy}>
              {busy ? 'Đang lưu…' : 'Xác nhận đã tiếp nhận'}
            </button>
          ))}
      </div>
    </div>
  );
}

// Modal danh sách ai đã / chưa xác nhận + thời điểm.
function AckListModal({ item, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    announcementAcks(item._id).then(setData).catch(() => setData({ rows: [], total: 0, ackedCount: 0 }));
  }, [item._id]);

  return (
    <div>
      <h3>👥 Tình trạng tiếp nhận — {item.title || 'Thông báo'}</h3>
      {!data ? (
        <div className="empty">Đang tải...</div>
      ) : (
        <>
          <div className="small muted" style={{ marginBottom: 10 }}>
            Đã tiếp nhận <b style={{ color: 'var(--ok)' }}>{data.ackedCount}</b> / {data.total} nhân viên
          </div>
          <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Nhân viên</th>
                <th>Phòng ban</th>
                <th className="c">Trạng thái</th>
                <th>Thời điểm</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length ? (
                data.rows.map((r) => (
                  <tr key={r.userId}>
                    <td>{r.name}</td>
                    <td className="small">{r.department || '—'}</td>
                    <td className="c">
                      <span className={`chip ${r.acked ? 'done' : 'todo'}`}>{r.acked ? 'Đã tiếp nhận' : 'Chưa'}</span>
                    </td>
                    <td className="small">{r.at ? fmtDateTime(r.at) : '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="empty">
                    Không có người nhận
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      )}
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
    </div>
  );
}

export default function AnnouncementsPage() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | active | expired
  const [mainTab, setMainTab] = useState('internal'); // internal | customer

  const canManage = me.role === 'GD' || me.role === 'TP'; // tạo thông báo
  const canDelete = me.role === 'GD' || me.role === 'CV'; // CHỈ GĐ/CV được xóa
  // Thông báo KHÁCH HÀNG: Giám đốc/Cố vấn hoặc phòng CSKH.
  const canCustAnn = me.role === 'GD' || me.role === 'CV' || (me.departments || []).includes('Phòng CSKH');

  const load = () => {
    setLoading(true);
    listAnnouncements()
      .then(setItems)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  async function del(id) {
    if (!await confirm('Xóa thông báo này?')) return;
    try {
      await removeAnnouncement(id);
      toast('Đã xóa thông báo');
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Thông báo</h2>
          <div className="sub">Thông báo nội bộ công ty — hiển thị trên dòng chạy ở đầu mọi màn.</div>
        </div>
        {canManage && mainTab === 'internal' && (
          <button className="btn" onClick={() => openModal(<CreateModal onClose={closeModal} onCreated={load} />)}>
            + Tạo thông báo
          </button>
        )}
      </div>

      {canCustAnn && (
        <div className="seg" style={{ marginBottom: 14 }}>
          <button className={mainTab === 'internal' ? 'active' : ''} onClick={() => setMainTab('internal')}>🔔 Nội bộ</button>
          <button className={mainTab === 'customer' ? 'active' : ''} onClick={() => setMainTab('customer')}>👥 Khách hàng</button>
        </div>
      )}

      {mainTab === 'customer' ? <CustomerAnnounce /> : (() => {
        const unread = items.filter((a) => a.canAck && !a.mineAcked).length;
        const shown = items.filter((a) => (filter === 'active' ? a.active : filter === 'expired' ? !a.active : true));
        return (
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="seg" style={{ margin: 0 }}>
            {[['all', `Tất cả (${items.length})`], ['active', 'Đang chạy'], ['expired', 'Đã hết']].map(([k, l]) => (
              <button key={k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>{l}</button>
            ))}
          </div>
          {unread > 0 && <span className="small muted"><b style={{ color: 'var(--danger)' }}>{unread}</b> thông báo bạn chưa xác nhận</span>}
        </div>
        {loading ? (
          <div className="empty">Đang tải...</div>
        ) : shown.length ? (
          shown.map((a) => (
            <div key={a._id} className="notif-row">
              <div className="notif-ico" style={{ background: a.active ? 'color-mix(in srgb, var(--accent) 13%, transparent)' : 'var(--panel2)', color: a.active ? 'var(--accent)' : 'var(--muted2)' }}>
                🔔
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
                  <span className={`chip ${a.active ? 'done' : 'todo'}`}>{a.active ? 'Đang chạy' : 'Đã hết'}</span>
                  {a.departments && a.departments.length ? (
                    a.departments.map((d) => (
                      <span className="tag" key={d}>{d}</span>
                    ))
                  ) : (
                    <span className="tag">Tất cả</span>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{a.title || a.content}</div>
                {a.title && <div className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.content}</div>}
                <div className="small muted mono" style={{ marginTop: 2 }}>
                  {a.createdByName || '—'} · chạy đến {fmtDateTime(a.endAt)}
                </div>
              </div>
              <a
                className="mono small"
                style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 700, flex: '0 0 auto' }}
                title="Xem ai đã / chưa tiếp nhận"
                onClick={() => openModal(<AckListModal item={a} onClose={closeModal} />, { wide: true })}
              >
                {a.ackedCount}/{a.total} đã nhận
              </a>
              <div className="row" style={{ gap: 6, flex: '0 0 auto' }}>
                <button
                  className="btn ghost sm"
                  onClick={() => openModal(<DetailModal item={a} me={me} onClose={closeModal} onChanged={load} />)}
                >
                  {a.canAck && !a.mineAcked ? 'Đọc & xác nhận' : 'Xem'}
                </button>
                {canDelete && (
                  <button className="btn ghost sm danger" onClick={() => del(a._id)}>
                    Xóa
                  </button>
                )}
              </div>
              {a.canAck && !a.mineAcked && <span className="notif-dot" title="Bạn chưa xác nhận" />}
            </div>
          ))
        ) : (
          <div className="empty">{filter === 'all' ? 'Chưa có thông báo nào' : 'Không có thông báo phù hợp'}</div>
        )}
      </div>
        );
      })()}
    </>
  );
}
